// @flow
import React from 'react'
import { connect } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import I18n from 'i18n-react'
import Alert from 'reactstrap/lib/Alert'
import Button from 'reactstrap/lib/Button'
import Modal from 'reactstrap/lib/Modal'
import ModalBody from 'reactstrap/lib/ModalBody'
import classNames from 'classnames'
import Loadable from 'react-loadable'
import GuardXLogger from 'guard-x-logger'

// config
import { config } from '../Config/ConfigLoader'

import packageJson from '../../package.json'
// submodules
import { cookie } from 'devx-js-utilities'

import { getNewSpecification, getStepUrl } from '../Core/ContainerFunctions'

// containers
import ProductDetailContainer from '../Containers/ProductDetailContainer'

// redux
import NavigationActions from '../Core/Redux/NavigationRedux'
import GeneralActions, { ERROR_TYPES, NOTIFICATION_TYPES } from '../Core/Redux/GeneralRedux'
import ProductActions from '../Core/Redux/ProductRedux'
import { WEB_TYPES_KEYS } from '../Core/Redux/SettingsRedux'
import ValidationActions from '../Core/Redux/ValidationRedux'
// components
import { GoogleTagManager, Loader, NavigationPanel, ValidatorError, ValidatorScript } from '../Components'
import { ShowDebugError, VariablesDebugBlock } from '../Components/_debug'

// selectors
import { selectIsAppRehydrated } from '../selectors/reduxPersist'
import { selectTrackingParams } from '../selectors/userData'

import { saveUtmParams as saveUtmParamsToApi } from '../helpers/general'

const QuoteListContainer = Loadable({
  loader: () => import('../Containers/QuoteListContainer'),
  loading() {
    return <span />
  },
})

const CalculatorDetailContainer = Loadable({
  loader: () => import('../Containers/CalculatorDetailContainer'),
  loading() {
    return <span />
  },
})

const ContractDraftContainer = Loadable({
  loader: () => import('../Containers/ContractDraftContainer'),
  loading() {
    return <span />
  },
})

const AcceptDraftContainer = Loadable({
  loader: () => import('../Containers/AcceptDraftContainer'),
  loading() {
    return <span />
  },
})

const ErrorContainer = Loadable({
  loader: () => import('../Containers/ErrorContainer'),
  loading() {
    return <span />
  },
})

// types
declare var API_LINK: string
declare var DEV: boolean
import type { Product } from '../Core/Types/Product'
import type { ActiveSettingsType } from '../Core/Types/SettingsTypes'
import type { LocationType } from '../Core/Types/LocationType'
import type { AllErrorType } from '../Core/Types/ErrorTypes'
import type { StepsType } from '../Core/Types/Navigation'
import type { ResourcesType, ResourceType } from '../Core/Types/Resources'
import type { NotificationType } from '../Core/Redux/GeneralRedux'
import { selectActiveSettings } from '../selectors/settings'
import WithUseSpecification from '../Components/WithUseSpecification'
import { getValueForPath } from '../Core/FilterFunctions'
import UserDataActions from '../Core/Redux/UserDataRedux'

type Props = {
  activeSettings: ActiveSettingsType,
  affiliate: string,
  allErrors: Array<AllErrorType>,
  clearNotifications: () => void,
  debug: boolean,
  errors: Array<AllErrorType>,
  errorTranslations: { [key: string]: string },
  fetching: number,
  generalValidationErrors: { [key: string]: { [key: string]: string | number } },
  history: any,
  isBackOffice: boolean,
  lastValidStep: Object,
  linkId: string,
  location: LocationType,
  notificationToShow: Array<NotificationType>,
  onActionFailure: (error: Object, errorType: number) => void,
  onChangeRedirectUrl: (redirectUrl: string) => void,
  onGetWebSettingsRequest: () => void,
  onResetLastValidStepExpire: () => void,
  onSetLastValidStepIndex: (key: string) => void,
  productName: string,
  products: Array<Product>,
  redirectUrl: string,
  rehydrated: boolean,
  setAffiliate: (string) => void,
  steps: StepsType,
  subProductName: string,
  testing: boolean,
  token: string,
  trackingParams: Object,
  translations: { [key: string]: string },
  webType: number,
}

type State = {
  showDebugVariables: boolean,
}

const _valuesBeforeHydration: Object = { policyHolder: {} }

class RootContainer extends React.Component<Props, State> {
  state = {
    showDebugVariables: false,
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (nextProps.redirectUrl !== '') {
      nextProps.onChangeRedirectUrl('')
      nextProps.history.push(
        nextProps.redirectUrl.endsWith('/') ? nextProps.redirectUrl : `${nextProps.redirectUrl}/`,
        nextProps.redirectState ? nextProps.redirectState : undefined,
      )
    }

    if (nextProps.location && nextProps.location.pathname && !nextProps.location.pathname.endsWith('/')) {
      nextProps.history.push(
        `${nextProps.location.pathname}/`,
        nextProps.redirectState ? nextProps.redirectState : undefined,
      )
    }

    if (!nextProps.location.pathname.includes('error')) {
      for (let index = 0; index < nextProps.errors.length; index++) {
        if (nextProps.errors[index].type === ERROR_TYPES.CRITICAL) {
          nextProps.onResetLastValidStepExpire()
          nextProps.onChangeRedirectUrl('/error')
          break
        }
      }
    }

    // poc.md - zapsano
    // if (!nextProps.rehydrated) {
    //   const vehicleTypeRadios = Array.from(document.getElementsByName('type'))
    //   if (vehicleTypeRadios && vehicleTypeRadios.length > 0) {
    //     vehicleTypeRadios.forEach((radio) => {
    //       if (radio.checked && radio.value !== 'car') {
    //         _valuesBeforeHydration.type = radio.value
    //       }
    //     })
    //   }
    //   const policyHolderTypeRadios = Array.from(document.getElementsByName('policyHolder-type'))
    //   if (policyHolderTypeRadios && policyHolderTypeRadios.length > 0) {
    //     policyHolderTypeRadios.forEach((radio) => {
    //       if (radio.checked && radio.value !== 'person') {
    //         _valuesBeforeHydration.policyHolder.type = radio.value
    //       }
    //     })
    //   }
    //   const cicEnabledInput = Array.from(document.getElementsByName('cicEnabled'))
    //   if (cicEnabledInput && cicEnabledInput.length === 1) {
    //     // $FlowIssue
    //     if (cicEnabledInput[0].checked) {
    //       _valuesBeforeHydration.cicEnabled = true
    //     }
    //   }
    // }
    return null
  }

  componentDidMount = () => {
    this.initValues()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      location,
      generalValidationErrors,
      trackingParams,
      rehydrated,
      translations,
      errorTranslations,
    } = this.props
    if (
      typeof window.scrollTo === 'function' &&
      (Object.keys(generalValidationErrors).length > Object.keys(prevProps.generalValidationErrors).length ||
        location !== prevProps.location)
    ) {
      window.scrollTo(0, 0)
    }
    if (trackingParams !== prevProps.trackingParams) {
      this.saveUtmParams()
    }

    if (rehydrated && !prevProps.rehydrated) {
      this.initValues()
    }
    if (prevProps.translations !== translations || prevProps.errorTranslations !== errorTranslations) {
      I18n.setTexts({
        ...I18n.texts,
        ...translations,
        validationErrors: errorTranslations,
      })
    }
  }

  initValues = () => {
    const {
      history,
      initApp,
      lastValidStep,
      location,
      onChangeRedirectUrl,
      onResetLastValidStepExpire,
      onSetLastValidStepIndex,
      steps,
      setAffiliate,
      affiliate,
    } = this.props
    initApp()

    if (location.state && location.state.message) {
      history.replace({ ...location, state: undefined })
    }

    if (!lastValidStep.expire) {
      for (let index = lastValidStep.index + 1; index < steps.length; index++) {
        if (location.pathname.indexOf(steps[index].key) >= 0) {
          onChangeRedirectUrl(steps[lastValidStep.index].url)
          break
        }
      }
    } else if (new Date(lastValidStep.expire).getTime() > Date.now()) {
      onChangeRedirectUrl(steps[lastValidStep.index].url)
    } else {
      onResetLastValidStepExpire()
      onSetLastValidStepIndex('product')
    }

    if (affiliate) {
      setAffiliate(affiliate)
    } else if (cookie.readCookie('affiliate')) {
      setAffiliate(cookie.readCookie('affiliate'))
    }
  }

  componentDidCatch(error, errorInfo) {
    GuardXLogger.captureException(error, { extra: errorInfo })
  }

  saveUtmParams = () => {
    const { trackingParams } = this.props
    saveUtmParamsToApi(trackingParams)
  }

  handleToggleDebugModal = () => {
    this.setState({ showDebugVariables: !this.state.showDebugVariables })
  }

  handleFetchResource = (resourceName: string): void | ResourceType => {
    const { resources } = this.props
    const resource = getValueForPath(resourceName, 0, resources)
    if (resource) {
      return resource
    }
  }

  handleOnInit = (initSpecification, validator) => {
    const { userDataInit, onChangeProductSpecification, rehydrated, userData } = this.props
    let specificationForChange = initSpecification
    if (rehydrated && validator) {
      const [, specification] = getNewSpecification(validator, userData)
      if (specification) {
        specificationForChange = specification
      }
    }
    userDataInit(specificationForChange)
    onChangeProductSpecification(specificationForChange)
  }

  render() {
    const {
      allErrors,
      clearNotifications,
      debug,
      fetching,
      generalValidationErrors,
      lastValidStep,
      location,
      notificationToShow,
      productName,
      rehydrated,
      showDebugVariables,
      steps,
      subProductName,
      testing,
      token,
      webType,
    } = this.props

    const classes = ['container']
    if (debug) {
      classes.push('mt-5')
    }

    const activeStepKey = steps.reduce((acc, step) => {
      if (location.pathname.replace(/\/$/, '') === getStepUrl(subProductName, step.key)) {
        return step.key
      } else if (location.pathname.includes('error')) {
        return 'error'
      }
      return acc
    }, '')

    const shouldDisplayNavigationPanel = !['product', 'error', 'accept'].includes(activeStepKey)
    return (
      <div className="body-full">
        {testing && <p>version: {packageJson.version}</p>}
        {DEV && (
          <div className="container mb-4">
            <div className="alert alert-sm alert-with-icon alert-warning m-auto" role="alert">
              <i className="icon icon-checkmark" />
              <span>
                TEST version: {packageJson.version}, URL: {API_LINK}
              </span>
            </div>
          </div>
        )}
        <div className={classNames(classes)}>
          {shouldDisplayNavigationPanel && (
            <div className="mt-3">
              <NavigationPanel
                step={lastValidStep}
                steps={steps}
                subProductName={subProductName}
                url={this.props.location.pathname}
              />
            </div>
          )}
          {((location.state && location.state.message) || Object.keys(generalValidationErrors).length > 0) && (
            <div className="mb-3 mt-3">
              {location.state && location.state.message && (
                <div className="alert alert-danger">
                  {I18n.translate(`validationErrors.${location.state.message}`, {
                    phoneNumber: I18n.translate('acceptScreen.phoneNumber', { context: webType }),
                  })}
                </div>
              )}
              {Object.keys(generalValidationErrors).map((errorKey) => (
                <div className="alert alert-danger" key={errorKey}>
                  {I18n.translate(`validationErrors.${errorKey}`)}
                </div>
              ))}
            </div>
          )}
        </div>
        {!rehydrated && <style type="text/css">{'.Select-arrow-zone { display: none }'}</style>}
        <Switch>
          <Route
            exact
            path="/"
            render={(props) => {
              return (
                <WithUseSpecification
                  fetchResource={this.handleFetchResource}
                  onInit={this.handleOnInit}
                  productName={productName}
                  token={token}
                >
                  {(validatorObject) => (
                    <ProductDetailContainer
                      {...props}
                      // beforeHydration={_valuesBeforeHydration}
                      location={props.location}
                      rehydrated={rehydrated}
                      saveUtmParams={this.saveUtmParams}
                      token={token}
                      validate={validatorObject.validate}
                      validatorLoaded={validatorObject.loaded}
                    />
                  )}
                </WithUseSpecification>
              )
            }}
          />
          <Route
            path={getStepUrl(subProductName, 'calculator')}
            render={(props) => (
              <CalculatorDetailContainer
                {...props}
                rehydrated={rehydrated}
                saveUtmParams={this.saveUtmParams}
                token={token}
              />
            )}
          />
          <Route
            path={getStepUrl(subProductName, 'quotes')}
            render={(props) => (
              <QuoteListContainer {...props} rehydrated={rehydrated} saveUtmParams={this.saveUtmParams} token={token} />
            )}
          />
          <Route
            path={getStepUrl(subProductName, 'contract')}
            render={(props) => (
              <ContractDraftContainer
                {...props}
                rehydrated={rehydrated}
                saveUtmParams={this.saveUtmParams}
                token={token}
              />
            )}
          />
          <Route
            path={getStepUrl(subProductName, 'accept')}
            render={(props) => <AcceptDraftContainer {...props} rehydrated={rehydrated} token={token} />}
          />
          <Route path="/error" render={(props) => <ErrorContainer {...props} token={token} />} />
          <Redirect to="/" />
        </Switch>
        {debug && (
          <Alert className="fixed-top w-25 text-center bg-light" color="info" style={{ right: 0, left: 'auto' }}>
            <div>
              Aplikace běží v Debug módu.
              <br />
              WebType: {WEB_TYPES_KEYS[this.props.webType]}. <br />
              BackOffice: {this.props.isBackOffice ? 'ano' : 'ne'}
              <br />
              <Button className="mt-2" color="info" onClick={this.handleToggleDebugModal} outline size="sm">
                Zobrazit aktivní nastavení
              </Button>
            </div>
          </Alert>
        )}
        <Modal isOpen={showDebugVariables} toggle={this.handleToggleDebugModal}>
          <ModalBody>
            <VariablesDebugBlock active={debug} title="activeSettings" variables={this.props.activeSettings} />
          </ModalBody>
        </Modal>
        <Modal
          className="d-flex vh-100 align-items-center mt-0"
          contentClassName="p-0 bg-transparent border-0 m-0 shadow-none"
          isOpen={notificationToShow.length > 0}
          toggle={clearNotifications}
        >
          {notificationToShow.map((notification: NotificationType, index: number) => {
            const alertClasses = ['mx-auto', 'alert', 'alert-sm', 'alert-with-icon', 'bg-white cursor-pointer']
            const iconClasses = ['icon']
            switch (notification.type) {
              case NOTIFICATION_TYPES.success:
                alertClasses.push('alert-success')
                iconClasses.push('icon-checkmark')
                break
              case NOTIFICATION_TYPES.info:
                alertClasses.push('alert-active-object', 'text-dark')
                iconClasses.push('pfp', 'pfp-info')
                break
              case NOTIFICATION_TYPES.error:
                alertClasses.push('alert-danger')
                iconClasses.push('pfp', 'pfp-close')
                break
            }
            return (
              <div
                className={classNames(alertClasses)}
                key={`alert-${index}`}
                onClick={clearNotifications}
                role="alert"
              >
                <i className={classNames(iconClasses)} />
                <span>
                  {notification.title && <strong>{notification.title}: </strong>}
                  {notification.message}
                </span>
              </div>
            )
          })}
        </Modal>
        {debug && (
          <div>
            {allErrors.map((error) => (
              <ShowDebugError active={debug} error={error} key={error.time} />
            ))}
          </div>
        )}
        <ValidatorError scroll={Object.keys(this.props.generalValidationErrors).length > 0} />
        <Loader active={fetching > 0} fullScreen />
        <input id="currentStep" type="hidden" value={activeStepKey} />
        <GoogleTagManager id="GTM-MSZ99BX" />
      </div>
    )
  }
}

// todo delete active settings from redux
// todo remove get Product state on every screen!
const mapStateToProps = (state) => {
  return {
    activeSettings: selectActiveSettings(state),
    allErrors: state.appData.general.allErrors,
    debug: state.appData.debug.debug,
    discounts: state.appData.discounts.discounts,
    errorTranslations: state.appData.general.errorTranslations,
    errors: state.appData.general.errors,
    fetching: state.appData.general.fetching,
    generalValidationErrors: state.appData.validation.generalValidationErrors,
    lastValidStep: state.appData.navigation.lastValidStep,
    notificationToShow: state.appData.general.notificationToShow,
    productName: state.appData.product.productName,
    redirectState: state.appData.general.redirectState,
    redirectUrl: state.appData.general.redirectUrl,
    rehydrated: selectIsAppRehydrated(state),
    resources: state.appData.product.resources,
    steps: state.appData.navigation.steps,
    testing: state.appData.debug.testing,
    trackingParams: selectTrackingParams(state),
    translations: state.appData.general.translations,
    userData: state.userData.data,
    webType: state.appData.settings.webType,
  }
}

const mapDispatchToProps = {
  clearNotifications: GeneralActions.clearNotifications,
  initApp: GeneralActions.initApp,
  onActionFailure: GeneralActions.onActionFailure,
  onChangeProductSpecification: ProductActions.onChangeProductSpecification,
  onChangeRedirectUrl: GeneralActions.onChangeRedirectUrl,
  onResetLastValidStepExpire: NavigationActions.onResetLastValidStepExpire,
  onSetLastValidStepIndex: NavigationActions.onSetLastValidStepIndex,
  setAffiliate: ProductActions.setAffiliate,
  userDataInit: UserDataActions.userDataInit,
}

export default connect(mapStateToProps, mapDispatchToProps)(RootContainer)
